/**
 * @file useTransferDocument.ts
 * @description Данные карточки перемещения: загрузка, строки, проведение и
 * отмена (phase19.23 этап B).
 *
 * Вынесено из страницы (SRP): странице остаётся раскладка документа, хуку —
 * запросы. Каждая мутация возвращает документ целиком, поэтому состояние
 * заменяем ответом сервера, а не правим руками — один источник правды.
 *
 * Отдельно обрабатывается 409 `lot_moved`: партию переставили между черновиком
 * и проведением. Молча подставить новый адрес нельзя (кладовщик подписывает
 * бумагу с конкретным местом), поэтому показываем расхождения и предлагаем
 * «Обновить строки» (`POST /:id/refresh-rows`).
 */

import { useCallback, useEffect, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  TRANSFER_LOT_MOVED_CODE,
  parseLotMovedItems,
  transfersApi,
  type AddTransferItemPayload,
  type TransferDto,
  type TransferLotMovedItem,
  type UpdateTransferItemPayload,
} from "../../../lib/api-transfers";
import { wmsErrorText } from "../../../lib/wms-error-text";

export function useTransferDocument(id: string | undefined) {
  const notify = useNotify();
  const [transfer, setTransfer] = useState<TransferDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [movedLots, setMovedLots] = useState<TransferLotMovedItem[]>([]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    transfersApi
      .findOne(id)
      .then((data) => {
        if (cancelled) return;
        setTransfer(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить перемещение"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  /** Общая обёртка мутаций: ответ сервера — новое состояние документа. */
  const run = useCallback(
    async (
      action: (transferId: string) => Promise<TransferDto>,
      errorText: string,
    ): Promise<boolean> => {
      if (!transfer) return false;
      setBusy(true);
      try {
        setTransfer(await action(transfer.id));
        return true;
      } catch (err) {
        notify.error(wmsErrorText(err, errorText));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [transfer, notify],
  );

  const addItem = useCallback(
    (payload: AddTransferItemPayload) =>
      run((tid) => transfersApi.addItem(tid, payload), "Не удалось добавить строку"),
    [run],
  );

  const updateItem = useCallback(
    (itemId: string, payload: UpdateTransferItemPayload) =>
      run(
        (tid) => transfersApi.updateItem(tid, itemId, payload),
        "Не удалось изменить строку",
      ),
    [run],
  );

  const removeItem = useCallback(
    (itemId: string) =>
      run((tid) => transfersApi.removeItem(tid, itemId), "Не удалось удалить строку"),
    [run],
  );

  const cancel = useCallback(
    () => run((tid) => transfersApi.cancel(tid), "Не удалось отменить перемещение"),
    [run],
  );

  /** «Обновить строки» из диалога `lot_moved`: переснять адреса партий. */
  const refreshRows = useCallback(async () => {
    const ok = await run(
      (tid) => transfersApi.refreshRows(tid),
      "Не удалось обновить строки",
    );
    if (ok) {
      setMovedLots([]);
      notify.success("Адреса в строках обновлены — проверьте и проведите документ");
    }
  }, [run, notify]);

  /** Проведение. 409 `lot_moved` — не ошибка ввода, а расхождение с фактом. */
  const commit = useCallback(async () => {
    if (!transfer) return;
    setCommitting(true);
    try {
      setTransfer(await transfersApi.commit(transfer.id));
      setMovedLots([]);
      notify.success(`Перемещение ${transfer.transferNumber} проведено`);
    } catch (err) {
      if (err instanceof ApiError && err.code === TRANSFER_LOT_MOVED_CODE) {
        const parsed = parseLotMovedItems(err.details);
        if (parsed.length > 0) {
          setMovedLots(parsed);
          return;
        }
      }
      notify.error(wmsErrorText(err, "Не удалось провести перемещение"));
    } finally {
      setCommitting(false);
    }
  }, [transfer, notify]);

  return {
    transfer,
    loading,
    error,
    busy,
    committing,
    movedLots,
    dismissMovedLots: () => setMovedLots([]),
    addItem,
    updateItem,
    removeItem,
    cancel,
    commit,
    refreshRows,
  };
}
